import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { post, get } from '@/lib/api-client';
import { useAuthContext } from '@/hooks/useAuthContext';
import type { User } from '@/types';

/**
 * Response from POST /api/admin/impersonate/:userId
 */
interface StartImpersonationResponse {
  success: boolean;
  data: {
    targetUser: User;
    expiresAt: string;
    remainingSeconds: number;
  };
}

/**
 * Response from GET /api/admin/impersonate/status
 */
interface ImpersonationStatusResponse {
  success: boolean;
  data: {
    isActive: boolean;
    targetUser?: User;
    expiresAt?: string;
    remainingSeconds?: number;
  };
}

/**
 * Single entry in impersonation history
 */
export interface ImpersonationHistoryEntry {
  id: string;
  adminUserId: string;
  adminName: string;
  adminEmail: string;
  targetUserId: string;
  targetName: string;
  targetEmail: string;
  startTime: string;
  endTime: string | null;
  durationSeconds: number | null;
  ipAddress: string;
}

/**
 * Response from GET /api/admin/impersonate/history
 */
interface ImpersonationHistoryResponse {
  success: boolean;
  data: {
    sessions: ImpersonationHistoryEntry[];
    total: number;
    page: number;
    pageSize: number;
  };
}

/**
 * Filters for impersonation history queries
 */
export interface ImpersonationHistoryFilters {
  page?: number;
  pageSize?: number;
  adminUserId?: string;
  targetUserId?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Hook to start an impersonation session.
 * POST /api/admin/impersonate/:userId
 */
export function useStartImpersonation() {
  const { startImpersonation } = useAuthContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await post<StartImpersonationResponse>(
        `/admin/impersonate/${userId}`
      );
      return response;
    },
    onSuccess: (response) => {
      const { targetUser, expiresAt } = response.data;
      startImpersonation(targetUser, expiresAt);
      // Clear all cached data so the new user context fetches fresh results
      queryClient.clear();
      navigate('/');
    },
  });
}

/**
 * Hook to stop the current impersonation session.
 * POST /api/admin/impersonate/stop
 * Restores admin identity and redirects to /admin/users.
 */
export function useStopImpersonation() {
  const { stopImpersonation, isImpersonating } = useAuthContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      const response = await post<{ success: boolean }>('/admin/impersonate/stop');
      return response;
    },
    onSuccess: () => {
      stopImpersonation();
      // Clear all cached data so the admin context fetches fresh results
      queryClient.clear();
      navigate('/admin/users');
    },
    onError: () => {
      // If the backend call fails, clear local state anyway.
      // The server-side session will expire on its own.
      if (isImpersonating) {
        stopImpersonation();
        navigate('/admin/users');
      }
    },
  });
}

/**
 * Hook to poll impersonation session status every 30 seconds.
 * Auto-stops the session when the backend reports it as inactive/expired.
 */
export function useImpersonationStatus() {
  const { isImpersonating, stopImpersonation } = useAuthContext();

  return useQuery({
    queryKey: ['impersonation', 'status'],
    queryFn: async () => {
      const response = await get<ImpersonationStatusResponse>(
        '/admin/impersonate/status'
      );
      return response.data;
    },
    enabled: isImpersonating,
    refetchInterval: isImpersonating ? 30_000 : false,
    onSuccess: (data) => {
      // Backend says session is no longer active — clean up local state
      if (!data.isActive && isImpersonating) {
        stopImpersonation();
      }
    },
  });
}

/**
 * Hook to fetch paginated impersonation history with optional filters.
 * GET /api/admin/impersonate/history
 */
export function useImpersonationHistory(filters: ImpersonationHistoryFilters = {}) {
  const queryParams = new URLSearchParams();

  if (filters.page) queryParams.append('page', String(filters.page));
  if (filters.pageSize) queryParams.append('pageSize', String(filters.pageSize));
  if (filters.adminUserId) queryParams.append('adminUserId', filters.adminUserId);
  if (filters.targetUserId) queryParams.append('targetUserId', filters.targetUserId);
  if (filters.startDate) queryParams.append('startDate', filters.startDate);
  if (filters.endDate) queryParams.append('endDate', filters.endDate);

  return useQuery({
    queryKey: ['impersonation', 'history', filters],
    queryFn: async () => {
      const response = await get<ImpersonationHistoryResponse>(
        `/admin/impersonate/history?${queryParams.toString()}`
      );
      return response.data;
    },
  });
}
